import React from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Message } from 'mage-i18n';
import { GridContainer, VerticalRhythm } from 'mage-components';
import { Typography, SpacerVertical } from '@klarna/bubble-ui';

import { APP_PATHS } from '../constants';

import { merchantRegion, hasNonKlarnaOneProducts } from '../selectors/merchant';
import { getParentPricedProducts, getChildrenPricedProducts } from '../selectors/products';

import BackLink from './Pricing/BackLink';
import {
  TableForParentPricedProduct,
  TableForChildrenPricedProduct,
  GlobalPricingFootnote,
} from './Pricing/PricingTable';

const tKey = 'products-fe.components.global_prices_dialog';

const Pricing = ({
  basePath,
  childrenPricedProducts,
  history,
  location,
  parentPricedProducts: rawParentPricedProducts,
  nonKlarnaOneProductsSelected,
  region,
}) => {
  const parentPricedProducts = rawParentPricedProducts.filter(
    p =>
      p.prices && (nonKlarnaOneProductsSelected ? !p.id.includes('_one') : p.id.includes('_one')),
  );
  return (
    <GridContainer padding={{ mobile: 4 }}>
      <VerticalRhythm spacing={{ mobile: 10, tablet: 18 }} />
      <Typography textToken="text-style/headings/titles/bold/primary">
        <Message path={`${tKey}.title_${region}`} />
      </Typography>
      <SpacerVertical spaceToken="space/100" />
      <Typography textToken="text-style/text/super-paragraphs/body/regular">
        <Message path={`${tKey}.subtitle`} />
      </Typography>
      <SpacerVertical spaceToken="space/100" />
      <BackLink
        onClick={() =>
          history.push(
            (get(location, 'state.referrer') || `${basePath}/${APP_PATHS.PRODUCT_SELECTION_PATH}`) +
              history.location.search,
          )
        }
      />
      {nonKlarnaOneProductsSelected &&
        childrenPricedProducts.map(product => {
          return (
            <React.Fragment key={product.id}>
              <SpacerVertical spaceToken="space/600" />
              <Typography textToken="text-style/headings/blocks/bold/grande">
                <Message path={`${tKey}.table.title.${product.id}`} />
              </Typography>
              <SpacerVertical spaceToken="space/300" />
              <TableForChildrenPricedProduct productOptions={product.options} />
            </React.Fragment>
          );
        })}
      {parentPricedProducts.map(product => {
        const localPrices = product.prices.filter(p => !p.global);
        const globalPriceInEuro = product.prices.find(p => p.global && p.currencyCode === 'EUR');
        const pricesToShow = globalPriceInEuro ? [...localPrices, globalPriceInEuro] : localPrices;

        return (
          <React.Fragment key={product.id}>
            <SpacerVertical spaceToken="space/600" />
            <Typography textToken="text-style/headings/blocks/bold/grande">
              <Message path={`${tKey}.table.title.${product.id}`} />
            </Typography>
            <SpacerVertical spaceToken="space/300" />
            <TableForParentPricedProduct prices={pricesToShow} productId={product.id} />
            <SpacerVertical spaceToken="space/100" />
            {globalPriceInEuro && <GlobalPricingFootnote price={globalPriceInEuro} />}
          </React.Fragment>
        );
      })}
    </GridContainer>
  );
};

Pricing.propTypes = {
  basePath: PropTypes.string.isRequired,
  childrenPricedProducts: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  nonKlarnaOneProductsSelected: PropTypes.bool.isRequired,
  parentPricedProducts: PropTypes.array.isRequired,
  region: PropTypes.oneOf(['eu', 'us']).isRequired,
};

export default compose(
  connect(state => ({
    childrenPricedProducts: getChildrenPricedProducts(state),
    parentPricedProducts: getParentPricedProducts(state),
    nonKlarnaOneProductsSelected: hasNonKlarnaOneProducts(state),
    region: merchantRegion(state),
  })),
)(Pricing);
